/**
 * Acción de REDUX que permite obtener los roles de un usuario.
 *
 * Defensiva: si users/{uid} no existe, o no tiene Rol, o el Rol referencia
 * un doc que no existe, despachamos ROLES.ERROR en lugar de dejar la app
 * en estado inconsistente con roles=undefined. Esto evita cascadas de
 * TypeErrors que pueden parecer "deslogueo" al usuario.
 */

import { applyUserScope } from '../../lib/user_scope';
import { ROLES } from '../../../application/types';
import { canonicalUserName, syncAuthDisplayName } from '../../lib/user_name';

export const roles = ({ uid }) => async ( dispatch, state, firebase ) => {

  dispatch({ type: ROLES.GETTING_ROLES });

  try {
    const db = firebase().firestore();
    const userSnap = await db.collection('users').doc(uid).get();

    if (!userSnap.exists) {
      const err = new Error(`users/${uid} no existe en Firestore`);
      err.code = 'user-doc-missing';
      throw err;
    }

    const userData = userSnap.data() || {};

    // El displayName de Auth solo se escribe al CREAR el usuario; si el admin
    // cambió el 'Nombre completo' en Firestore, la sesión seguía mostrando (y
    // estampando en contabilizado) el nombre viejo. Re-sincronizamos aquí,
    // ANTES de que Router despache AUTH.LOGGEDIN con el objeto de Auth, para
    // que el header y todo consumidor de displayName vean el nombre vigente.
    // Best-effort: nunca rompe el login.
    await syncAuthDisplayName(
      firebase().auth().currentUser,
      canonicalUserName(userData, null)
    );

    const rolRef = userData['Rol'];

    if (!rolRef || typeof rolRef.get !== 'function') {
      const err = new Error(`users/${uid}.Rol no es una referencia válida`);
      err.code = 'role-ref-invalid';
      throw err;
    }

    const rolSnap = await rolRef.get();

    if (!rolSnap.exists) {
      // Discrepancia entre entornos: el users/{uid} apunta a un rol que
      // no existe en este Firestore (clásico al migrar de pruebas a prod
      // sin migrar el catálogo de roles).
      const rolPath = rolRef.path || 'desconocido';
      const err = new Error(`El rol referenciado (${rolPath}) no existe en este entorno. Contacte al administrador.`);
      err.code = 'role-doc-missing';
      throw err;
    }

    const rolData = rolSnap.data();
    if (!rolData) {
      const err = new Error('El rol existe pero está vacío');
      err.code = 'role-doc-empty';
      throw err;
    }

    // Alcance y SinToken viven en el usuario, no en el rol (spec 2026-10-05).
    return dispatch({ type: ROLES.GETTED_ROLES, roles: { ...applyUserScope(rolData, userData), id: rolRef.id } });
  } catch (error) {
    dispatch({
      type: ROLES.ERROR,
      code: error.code || 'unknown',
      message: error.message || 'Error desconocido cargando roles',
    });
    throw error;
  }
};